import { assertSafeIntegerInRange } from '../integers';

/**
 * What a player is told before the roulette charges them (gate V24C).
 *
 * ## Why the sentences live here and not in a component
 *
 * Same reason as `store/alpha-terms.ts`, which says it outright: a sentence
 * about odds, about duplicates or about a reset is a PROMISE, and a promise
 * written twice is a promise that eventually disagrees with itself. Every
 * string below is rendered by the interface and composed by nothing else.
 *
 * ## Nothing here is a number the draw uses
 *
 * There is no price, no weight and no probability in this module. The banner
 * response carries the integer weights the server actually draws against, and
 * every function here is arithmetic ON THAT - basis points in, basis points
 * out, in integers, exactly like the socket table itself. A constant here would
 * be a second source of truth for a number the server already publishes, and it
 * would go stale the first time a weight moved.
 *
 * ## Percentages are rounded DOWN, and the screen says so
 *
 * Three equal weights of 1000 in a pool of 3000 is exactly one third each, and
 * one third is not a terminating decimal. `skinBannerChanceBps` already floors,
 * so a screen printing three chances shows 33,33% three times and they sum to
 * 99,99%. That is a fact about DISPLAY, never about the draw: the draw walks
 * three contiguous integer bands of exactly 1000 points.
 *
 * The interface must therefore lead with the exact statement - "1 em 3 para cada
 * skin" - and offer the decimal beside it with the rounding named. Publishing
 * 99,99% with no explanation would read as one skin quietly being worse.
 */

/* -------------------------------------------------------------------------- */
/*  Labels                                                                    */
/* -------------------------------------------------------------------------- */

/** The basis-point domain. 10 000 bps IS 100%. */
export const DISCLOSURE_BPS_TOTAL = 10_000;

/**
 * `"33,33%"` for a chance in basis points, in the language the game speaks.
 *
 * Two decimals, always, including the trailing zeros: a column of `9%`,
 * `12,5%` and `33,33%` is a column a reader has to align by eye. Integer
 * arithmetic throughout - a percentage that went through a float would be the
 * one number on the screen nobody could reproduce.
 */
export function bpsPercentLabel(bps: number): string {
  assertSafeIntegerInRange(bps, 0, DISCLOSURE_BPS_TOTAL, 'chance in basis points');
  const whole = Math.trunc(bps / 100);
  const fraction = bps % 100;
  return `${whole},${fraction.toString().padStart(2, '0')}%`;
}

/**
 * `"1 em 3"` when a weight divides its total exactly, and null when it does not.
 *
 * Null rather than a rounded figure, deliberately. "1 em 3" is the sentence
 * that makes three equal chances legible without a decimal, and it is only
 * honest while the division is exact. A pool of 1000/1000/1001 would produce
 * "1 em 3" for all three and hide the difference - so it produces nothing, and
 * the interface falls back to the percentage.
 */
export function exactOneInLabel(weight: number, total: number): string | null {
  if (!Number.isSafeInteger(weight) || !Number.isSafeInteger(total) || weight <= 0 || total <= 0) {
    return null;
  }
  return total % weight === 0 ? `1 em ${total / weight}` : null;
}

/* -------------------------------------------------------------------------- */
/*  The joint table                                                           */
/* -------------------------------------------------------------------------- */

/** One skin's chance, as the banner published it. */
export interface DisclosurePoolEntry {
  readonly definitionId: string;
  readonly displayName: string;
  readonly weight: number;
  readonly chanceBps: number;
}

/** One socket capacity's chance, as the banner published it. */
export interface DisclosureSocketEntry {
  readonly sockets: number;
  readonly weight: number;
  readonly chanceBps: number;
}

/** One of the eighteen outcomes a roll can have. */
export interface SkinGachaJointOutcome {
  readonly definitionId: string;
  readonly displayName: string;
  readonly sockets: number;
  /**
   * The chance of THIS skin with THIS capacity, in basis points, rounded down.
   *
   * Computed from the two integer weights against the two integer totals in one
   * expression, so it is the product of the real probabilities rather than the
   * product of two already-rounded percentages. Multiplying 3333 by 2500 and
   * dividing would compound both roundings and drift.
   */
  readonly chanceBps: number;
}

/**
 * Every combination of skin and capacity, in the order the banner listed them.
 *
 * Eighteen rows for three skins, and the interface shows all of them: a player
 * asking "qual a chance de Cyber Rainbow com 6 sockets" must be able to read the
 * answer rather than multiply two numbers in their head.
 *
 * The two draws are INDEPENDENT on the server - separate entropy against
 * separate domains - which is exactly what makes a plain product correct here.
 */
export function skinGachaJointOutcomes(
  pool: readonly DisclosurePoolEntry[],
  socketOdds: readonly DisclosureSocketEntry[],
): readonly SkinGachaJointOutcome[] {
  const poolTotal = pool.reduce((sum, entry) => sum + entry.weight, 0);
  const socketTotal = socketOdds.reduce((sum, entry) => sum + entry.weight, 0);
  if (poolTotal <= 0 || socketTotal <= 0) {
    return [];
  }
  const outcomes: SkinGachaJointOutcome[] = [];
  for (const skin of pool) {
    for (const capacity of socketOdds) {
      outcomes.push({
        definitionId: skin.definitionId,
        displayName: skin.displayName,
        sockets: capacity.sockets,
        chanceBps: Math.floor(
          (skin.weight * capacity.weight * DISCLOSURE_BPS_TOTAL) / (poolTotal * socketTotal),
        ),
      });
    }
  }
  return outcomes;
}

/* -------------------------------------------------------------------------- */
/*  The same eighteen numbers, as a matrix                                    */
/* -------------------------------------------------------------------------- */

/** One column header: which skin, and what to call it. */
export interface SkinGachaMatrixSkin {
  readonly definitionId: string;
  readonly displayName: string;
}

/** One cell: this skin, at this row's capacity. */
export interface SkinGachaMatrixCell {
  readonly definitionId: string;
  readonly displayName: string;
  readonly chanceBps: number;
}

/** One row: a capacity, and its chance for each skin in column order. */
export interface SkinGachaMatrixRow {
  readonly sockets: number;
  readonly cells: readonly SkinGachaMatrixCell[];
}

export interface SkinGachaJointMatrix {
  /** The columns, in the order the server sent the pool. */
  readonly skins: readonly SkinGachaMatrixSkin[];
  /** The rows, in capacity order. */
  readonly rows: readonly SkinGachaMatrixRow[];
  /**
   * Whether every row's cells are equal to each other.
   *
   * True while the pool's weights are equal, which is the whole reason a reader
   * sees three identical numbers across a row. The screen owes that explanation
   * - "each skin is one in three and the capacity is drawn separately" - and it
   * may only say it while this is true. With unequal weights the cells really
   * do differ and the sentence would be a lie.
   */
  readonly rowsAreUniform: boolean;
}

/**
 * The eighteen combinations, arranged as capacities x skins.
 *
 * ## Why a matrix and not a list
 *
 * The same information, in a third of the height. Eighteen vertical rows needed
 * a scrolling box of their own, and that box pushed the reset, the recredit and
 * the two buttons below the fold - so the screen that has to be read in full
 * before a charge was the screen a player saw the top of.
 *
 * Nothing is removed and nothing is summarised: all eighteen probabilities are
 * cells, every cell is a real number, and the values are byte-identical to what
 * {@link skinGachaJointOutcomes} produces. This is a different arrangement of
 * one truth, not a simplification of it.
 */
export function skinGachaJointMatrix(
  pool: readonly DisclosurePoolEntry[],
  socketOdds: readonly DisclosureSocketEntry[],
): SkinGachaJointMatrix {
  const outcomes = skinGachaJointOutcomes(pool, socketOdds);
  if (outcomes.length === 0) {
    return { skins: [], rows: [], rowsAreUniform: false };
  }
  const skins = pool.map((entry) => ({
    definitionId: entry.definitionId,
    displayName: entry.displayName,
  }));
  const byKey = new Map(
    outcomes.map((outcome) => [`${outcome.definitionId}/${outcome.sockets}`, outcome]),
  );
  const rows = socketOdds.map((capacity) => ({
    sockets: capacity.sockets,
    // Column order follows the pool, so a header and its cells cannot drift.
    cells: skins.map((skin) => {
      const found = byKey.get(`${skin.definitionId}/${capacity.sockets}`);
      return {
        definitionId: skin.definitionId,
        displayName: skin.displayName,
        chanceBps: found?.chanceBps ?? 0,
      };
    }),
  }));
  return {
    skins,
    rows,
    rowsAreUniform: rows.every(
      (row) => new Set(row.cells.map((cell) => cell.chanceBps)).size === 1,
    ),
  };
}

/**
 * Whether every skin in the pool carries the same weight.
 *
 * The interface says "chances iguais" only while this is true. It is a question
 * about the WEIGHTS, never about the published percentages: three weights of
 * 1000 all publish 33,33% and so would 1000/1000/1001, and only one of those
 * pairs is actually equal.
 */
export function skinGachaWeightsAreEqual(pool: readonly DisclosurePoolEntry[]): boolean {
  if (pool.length === 0) {
    return false;
  }
  const [first] = pool;
  return pool.every((entry) => entry.weight === first?.weight);
}

/**
 * The share of copies born with one or two sockets, in basis points.
 *
 * Published because it is the number that hurts and because saying it AFTER the
 * purchase is how a calibrated expectation becomes a complaint. Under the
 * shipped profile it is 25% + 22% = 47%, and it is read off the banner rather
 * than written down here so it follows the table if the table ever moves.
 */
export const SKIN_GACHA_LOW_SOCKET_CAPACITIES: readonly number[] = Object.freeze([1, 2]);

export function skinGachaLowSocketShareBps(socketOdds: readonly DisclosureSocketEntry[]): number {
  const total = socketOdds.reduce((sum, entry) => sum + entry.weight, 0);
  if (total <= 0) {
    return 0;
  }
  const low = socketOdds
    .filter((entry) => SKIN_GACHA_LOW_SOCKET_CAPACITIES.includes(entry.sockets))
    .reduce((sum, entry) => sum + entry.weight, 0);
  return Math.floor((low * DISCLOSURE_BPS_TOTAL) / total);
}

/* -------------------------------------------------------------------------- */
/*  The sentences                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The fixed wording, in the order it has to be read.
 *
 * No superlatives, no urgency, no "quase garantido" and no approximate number
 * where an exact one exists. Two of them exist to REFUSE a sentence rather than
 * to add one: a skin must not be called purely cosmetic - sockets are for
 * jewels that may grant attributes in the hunt - and it must not be implied
 * that more sockets is stronger, which is false today and false for ever in a
 * duel.
 */
export const SKIN_GACHA_DISCLOSURE = Object.freeze({
  /** Both draws, and the one misconception worth naming. */
  independence:
    'A skin e a quantidade de sockets são sorteadas separadamente. Tirar a skin que você queria não muda a chance de sockets, e o contrário também vale.',
  /** Why three identical percentages are shown as 33,33% and not as 33,34%. */
  rounding:
    'As três skins têm exatamente a mesma chance: 1 em 3. A porcentagem é arredondada para baixo na exibição, por isso as três linhas somam 99,99% em vez de 100%. O sorteio usa os pesos inteiros, não a porcentagem.',
  /** A second copy is a different item, and that is the point of rolling again. */
  duplicates:
    'Você pode receber uma skin que já tem. A cópia nova é um item separado, com a própria quantidade de sockets — que pode ser maior ou menor que a da sua cópia atual.',
  /** Empty seats, and nothing to put in them. */
  emptySockets:
    'Os sockets nascem vazios. As joias que vão ocupá-los ainda não existem no jogo, e nenhuma cópia concede bônus por ter mais sockets.',
  /** Says what a skin does not do today, without promising what it never will. */
  noAdvantage:
    'A skin troca a aparência do casco. Ela não altera dano, vida, velocidade nem cadência, e no PvP não muda nada. Se as joias existirem um dia, o que elas fizerem será anunciado antes.',
  /** Bound at the grant, from its provenance. */
  soulbound:
    'A cópia é sua e fica presa à conta: não pode ser vendida, trocada nem anunciada no mercado.',
  /** The absence, stated. */
  noPity:
    'Não existe garantia por quantidade de rolagens. Cada rolagem é independente das anteriores.',
  /**
   * The label the price carries while the alpha lasts.
   *
   * `Alpha Fechado`, capitalised the way `store/alpha-terms.ts` capitalises it -
   * "todo o progresso do Alpha Fechado será apagado". A badge is not a
   * sentence, so it carries no full stop.
   */
  alphaPrice: 'Preço durante o Alpha Fechado',
  /**
   * The confirmation's own sentence.
   *
   * ## Why "Não há reembolso" had to go
   *
   * It used to end "A cobrança é definitiva ... Não há reembolso". Two
   * paragraphs below it, the same panel promises that purchased Cash comes back
   * at 100% into the open alpha and again with 30% more at launch. A player
   * reading both cannot tell which one is true.
   *
   * Both ARE true, and they are about different things: a roll cannot be undone,
   * and the recredit is about Cash rather than about a roll. So the sentence now
   * says exactly what cannot happen - the roll cannot be cancelled after the
   * confirmation - and points AT the rules instead of appearing to deny them.
   *
   * The economic rule did not change and no individual refund is promised.
   */
  confirmation:
    'A rolagem não pode ser cancelada depois da confirmação. O resultado é decidido pelo servidor e a skin não será equipada automaticamente. As regras de reset e recrédito exibidas anteriormente continuam válidas.',
});

/**
 * The sentence about how many copies are born narrow, built from the banner.
 *
 * A function rather than a constant because the number is the table's, not
 * this module's: a profile change has to move the sentence with it.
 */
export function skinGachaLowSocketSentence(socketOdds: readonly DisclosureSocketEntry[]): string {
  const share = bpsPercentLabel(skinGachaLowSocketShareBps(socketOdds));
  return `${share} das cópias nascem com 1 ou 2 sockets. A maioria das rolagens entrega uma cópia estreita, e isso é o esperado.`;
}
